// Diff helper for BOM-correction review: compare seeded prior draft vs proposed
// full-replace draft so the operator sees Changed / Same / Added / Removed.

import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { DictatedDraftLine, DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";

export type BomLineChangeKind = "same" | "changed" | "added" | "removed";

export type BomLineDiffRow = {
  key: string;
  kind: BomLineChangeKind;
  prior: DictatedDraftLine | null;
  next: DictatedDraftLine | null;
};

function lineKey(line: DictatedDraftLine, index: number): string {
  const mat =
    line.match.kind === "matched" ? line.match.material_id : line.material_name_heard.trim().toLowerCase();
  const slot = normalizeSlotLabel(line.slot_label);
  return `${mat}::${slot}::${index}`;
}

function softKey(line: DictatedDraftLine): string {
  const mat =
    line.match.kind === "matched"
      ? line.match.material_id
      : line.material_name_heard.trim().toLowerCase();
  return `${mat}::${normalizeSlotLabel(line.slot_label)}`;
}

function qtyClose(a: number, b: number): boolean {
  return Math.abs(Number(a) - Number(b)) < 1e-6;
}

function lineSame(a: DictatedDraftLine, b: DictatedDraftLine): boolean {
  return (
    softKey(a) === softKey(b) &&
    qtyClose(a.quantity, b.quantity) &&
    a.role === b.role &&
    (a.upgrade_price_delta ?? null) === (b.upgrade_price_delta ?? null)
  );
}

/**
 * Pair prior → next lines for a full-replace BOM correction review.
 * Prefers stable material+slot matching; falls back to order for duplicates.
 */
export function diffBomCorrectionDrafts(
  prior: DictatedRecipeDraft,
  next: DictatedRecipeDraft,
): BomLineDiffRow[] {
  const priorUsed = new Set<number>();
  const rows: BomLineDiffRow[] = [];

  next.lines.forEach((nLine, nIdx) => {
    let matchIdx = -1;
    // Prefer unused prior with same soft key
    for (let i = 0; i < prior.lines.length; i++) {
      if (priorUsed.has(i)) continue;
      if (softKey(prior.lines[i]) === softKey(nLine)) {
        matchIdx = i;
        break;
      }
    }
    // Fall back to same index if still free
    if (matchIdx < 0 && nIdx < prior.lines.length && !priorUsed.has(nIdx)) {
      matchIdx = nIdx;
    }

    if (matchIdx >= 0) {
      priorUsed.add(matchIdx);
      const pLine = prior.lines[matchIdx];
      rows.push({
        key: lineKey(nLine, nIdx),
        kind: lineSame(pLine, nLine) ? "same" : "changed",
        prior: pLine,
        next: nLine,
      });
      return;
    }

    rows.push({
      key: lineKey(nLine, nIdx),
      kind: "added",
      prior: null,
      next: nLine,
    });
  });

  prior.lines.forEach((pLine, pIdx) => {
    if (priorUsed.has(pIdx)) return;
    rows.push({
      key: `removed-${lineKey(pLine, pIdx)}`,
      kind: "removed",
      prior: pLine,
      next: null,
    });
  });

  return rows;
}

export function summarizeBomCorrectionDiff(rows: readonly BomLineDiffRow[]): {
  changed: number;
  same: number;
  added: number;
  removed: number;
} {
  let changed = 0;
  let same = 0;
  let added = 0;
  let removed = 0;
  for (const r of rows) {
    if (r.kind === "changed") changed += 1;
    else if (r.kind === "same") same += 1;
    else if (r.kind === "added") added += 1;
    else removed += 1;
  }
  return { changed, same, added, removed };
}
